import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api.js';

interface HourData {
  hour: number;
  avgOrders: number; avgUnits: number; avgRevenue: number; avgPrice: number;
  totalOrders: number; totalUnits: number; totalRevenue: number;
  totalCogs: number; totalProfit: number;
  margin: number; roi: number;
  salesPct: number; orderPct: number;
}

interface DowData {
  dow: number; name: string;
  avgOrders: number; avgUnits: number; avgRevenue: number; avgPrice: number;
  totalOrders: number; totalUnits: number; totalRevenue: number;
  totalCogs: number; totalProfit: number;
  margin: number; roi: number;
  conversion: number; sessions: number;
  salesPct: number; orderPct: number;
}

interface CatalogProduct { asin: string; sku: string; title: string }
interface ProductGroup { label: string; asins: string[]; products: CatalogProduct[] }

const MARKETPLACE_LABELS: Record<string, string> = {
  ATVPDKIKX0DER: 'US', A2EUQ1WTGCTBG2: 'CA', A1AM78C64UM0Y8: 'MX',
  A1F83G8C2ARO7P: 'UK', A1PA6795UKMFR9: 'DE', A13V1IB3VIYZZH: 'FR',
  APJ6JRA9NG5V4: 'IT', A1RKKUPIHCS9HS: 'ES', A1805IZSGTT6HS: 'NL', A2NODRKZP88ZB9: 'SE',
};

const fmtUsd = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
const fmtNum = (n: number) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(n);
function hourLabel(h: number): string {
  if (h === 0) return '12am';
  if (h === 12) return '12pm';
  return h < 12 ? `${h}am` : `${h - 12}pm`;
}

function groupProducts(products: CatalogProduct[]): ProductGroup[] {
  const seen = new Set<string>();
  const filtered = products.filter(p => {
    if (!p.title?.trim() || seen.has(p.asin)) return false;
    seen.add(p.asin);
    return true;
  });
  const groups = new Map<string, CatalogProduct[]>();
  for (const p of filtered) {
    const label = p.title.includes('Walkie') ? 'Walkie Talkies'
      : p.title.includes('CarPlay') || p.title.includes('Android') ? 'CarPlay Adapter'
      : p.title.slice(0, 30);
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label)!.push(p);
  }
  return [...groups.entries()].map(([label, prods]) => ({
    label, asins: [...new Set(prods.map(p => p.asin))], products: prods,
  }));
}

// ── Column config ───────────────────────────────────────────────────────────

interface RowDef {
  key: string; label: string;
  getHourValue: (h: HourData) => number;
  getDowValue: (d: DowData) => number;
  format: (v: number) => string;
  barMax?: number; barColor?: string;
  defaultVisible?: boolean;
}

const STORAGE_KEY = 'sellvana_dayparting_rows';

function loadRowConfig(allKeys: string[]): { visible: string[]; order: string[] } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return { visible: allKeys, order: allKeys };
}
function saveRowConfig(cfg: { visible: string[]; order: string[] }) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg));
}

function RowConfig({ config, allRows, onChange }: {
  config: { visible: string[]; order: string[] };
  allRows: RowDef[];
  onChange: (cfg: { visible: string[]; order: string[] }) => void;
}) {
  const [open, setOpen] = useState(false);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const ordered = config.order.map(k => allRows.find(r => r.key === k)!).filter(Boolean);

  const toggle = (key: string) => {
    const vis = config.visible.includes(key) ? config.visible.filter(k => k !== key) : [...config.visible, key];
    const next = { ...config, visible: vis };
    onChange(next); saveRowConfig(next);
  };
  const move = (from: number, to: number) => {
    const order = [...config.order];
    const [item] = order.splice(from, 1);
    order.splice(to, 0, item);
    const next = { ...config, order };
    onChange(next); saveRowConfig(next);
  };

  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)}
        className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 flex items-center gap-1">
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
        </svg>
        Rows
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full z-50 mt-1 w-56 rounded-xl border border-gray-200 bg-white shadow-lg p-2 max-h-80 overflow-y-auto">
            <p className="text-[10px] uppercase text-gray-400 font-semibold px-2 py-1">Toggle & drag to reorder</p>
            {ordered.map((row, i) => (
              <div key={row.key} draggable
                onDragStart={() => setDragIdx(i)} onDragOver={e => e.preventDefault()}
                onDrop={() => { if (dragIdx !== null && dragIdx !== i) move(dragIdx, i); setDragIdx(null); }}
                className={`flex items-center gap-2 rounded-lg px-2 py-1.5 cursor-grab hover:bg-gray-50 ${dragIdx === i ? 'opacity-50' : ''}`}>
                <input type="checkbox" checked={config.visible.includes(row.key)} onChange={() => toggle(row.key)}
                  className="rounded border-gray-300 text-brand-600 h-3.5 w-3.5" />
                <span className="text-xs text-gray-700 flex-1">{row.label}</span>
                <svg className="w-3 h-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                </svg>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Component ───────────────────────────────────────────────────────────────

export function SalesDayparting() {
  const [start, setStart] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [end, setEnd] = useState(() => new Date().toISOString().slice(0, 10));
  const [activeDays, setActiveDays] = useState<number | null>(30);
  const [marketplace, setMarketplace] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedAsin, setSelectedAsin] = useState('');
  const [availableMarkets, setAvailableMarkets] = useState<string[]>([]);
  const [productGroups, setProductGroups] = useState<ProductGroup[]>([]);

  const [hourData, setHourData] = useState<HourData[]>([]);
  const [dowData, setDowData] = useState<DowData[]>([]);
  const [totalDays, setTotalDays] = useState(0);
  const [peakHour, setPeakHour] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'hourly' | 'dow'>('hourly');

  // Load filters once
  useEffect(() => {
    api.post<{ rows: Array<{ marketplace: string }> }>(
      '/admin/query',
      { sql: "SELECT DISTINCT marketplace FROM amazon_orders WHERE marketplace IS NOT NULL ORDER BY marketplace" },
    ).then(r => setAvailableMarkets(r.rows.map(row => row.marketplace))).catch(() => {});

    api.post<{ rows: CatalogProduct[] }>(
      '/admin/query',
      { sql: "SELECT c.asin, c.sku, c.title FROM amazon_catalog c WHERE c.asin IS NOT NULL AND c.title IS NOT NULL AND c.title != '' AND c.sku NOT LIKE 'amzn.gr.%' GROUP BY c.asin ORDER BY c.title" },
    ).then(r => setProductGroups(groupProducts(r.rows))).catch(() => {});
  }, []);

  const activeGroup = productGroups.find(g => g.label === selectedGroup);
  const effectiveAsin = selectedAsin || (activeGroup ? activeGroup.asins.join(',') : '');

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ start, end });
    if (marketplace) params.set('marketplace', marketplace);
    if (effectiveAsin) params.set('asin', effectiveAsin);
    api.get<{ hours: HourData[]; byDow: DowData[]; totalDays: number; peakHour: number }>(`/sales/dayparting?${params}`)
      .then(r => { setHourData(r.hours); setDowData(r.byDow); setTotalDays(r.totalDays); setPeakHour(r.peakHour); })
      .finally(() => setLoading(false));
  }, [start, end, marketplace, effectiveAsin]);

  useEffect(() => { load(); }, [load]);

  const maxHourRevenue = Math.max(...hourData.map(h => h.avgRevenue), 1);
  const maxHourOrders = Math.max(...hourData.map(h => h.avgOrders), 1);
  const maxDowRevenue = Math.max(...dowData.map(d => d.avgRevenue), 1);
  const maxDowOrders = Math.max(...dowData.map(d => d.avgOrders), 1);

  const ALL_ROWS: RowDef[] = [
    { key: 'avgOrders', label: 'Avg Orders', getHourValue: h => h.avgOrders, getDowValue: d => d.avgOrders, format: fmtNum, barMax: tab === 'hourly' ? maxHourOrders : maxDowOrders, barColor: '#3b82f6', defaultVisible: true },
    { key: 'avgUnits', label: 'Avg Units', getHourValue: h => h.avgUnits, getDowValue: d => d.avgUnits, format: fmtNum, defaultVisible: true },
    { key: 'avgRevenue', label: 'Avg Revenue', getHourValue: h => h.avgRevenue, getDowValue: d => d.avgRevenue, format: fmtUsd, barMax: tab === 'hourly' ? maxHourRevenue : maxDowRevenue, barColor: '#10b981', defaultVisible: true },
    { key: 'avgPrice', label: 'Avg Price', getHourValue: h => h.avgPrice, getDowValue: d => d.avgPrice, format: fmtUsd, defaultVisible: false },
    { key: 'margin', label: 'Margin %', getHourValue: h => h.margin, getDowValue: d => d.margin, format: v => `${v.toFixed(1)}%`, defaultVisible: true },
    { key: 'roi', label: 'ROI %', getHourValue: h => h.roi, getDowValue: d => d.roi, format: v => `${v.toFixed(1)}%`, defaultVisible: true },
    { key: 'conversion', label: 'Conversion %', getHourValue: () => 0, getDowValue: d => d.conversion, format: v => v > 0 ? `${v.toFixed(2)}%` : '-', defaultVisible: true },
    { key: 'salesPct', label: 'Sales %', getHourValue: h => h.salesPct, getDowValue: d => d.salesPct, format: v => `${v.toFixed(1)}%`, defaultVisible: true },
    { key: 'orderPct', label: 'Order %', getHourValue: h => h.orderPct, getDowValue: d => d.orderPct, format: v => `${v.toFixed(1)}%`, defaultVisible: false },
    { key: 'totalOrders', label: 'Total Orders', getHourValue: h => h.totalOrders, getDowValue: d => d.totalOrders, format: v => fmtNum(v), defaultVisible: false },
    { key: 'totalUnits', label: 'Total Units', getHourValue: h => h.totalUnits, getDowValue: d => d.totalUnits, format: v => fmtNum(v), defaultVisible: false },
    { key: 'totalRevenue', label: 'Total Revenue', getHourValue: h => h.totalRevenue, getDowValue: d => d.totalRevenue, format: fmtUsd, defaultVisible: false },
    { key: 'totalCogs', label: 'Total COGS', getHourValue: h => h.totalCogs, getDowValue: d => d.totalCogs, format: fmtUsd, defaultVisible: false },
    { key: 'totalProfit', label: 'Total Profit', getHourValue: h => h.totalProfit, getDowValue: d => d.totalProfit, format: fmtUsd, defaultVisible: false },
  ];

  const [rowConfig, setRowConfig] = useState(() => loadRowConfig(ALL_ROWS.filter(r => r.defaultVisible !== false).map(r => r.key)));
  const visibleRows = rowConfig.order.filter(k => rowConfig.visible.includes(k)).map(k => ALL_ROWS.find(r => r.key === k)!).filter(Boolean);

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-600">From</label>
          <input type="date" value={start} onChange={e => { setStart(e.target.value); setActiveDays(null); }}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm" />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-600">To</label>
          <input type="date" value={end} onChange={e => { setEnd(e.target.value); setActiveDays(null); }}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm" />
        </div>
        <div className="flex gap-2">
          {[
            { key: '7d', label: '7d', days: 7 },
            { key: '14d', label: '14d', days: 14 },
            { key: '30d', label: '30d', days: 30 },
            { key: '60d', label: '60d', days: 60 },
            { key: '90d', label: '90d', days: 90 },
          ].map(({ key, label, days }) => (
            <button key={key} onClick={() => {
              const s = new Date(); s.setDate(s.getDate() - days);
              setStart(s.toISOString().slice(0, 10));
              setEnd(new Date().toISOString().slice(0, 10));
              setActiveDays(days);
            }} className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
              activeDays === days ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}>{label}</button>
          ))}
        </div>
        {/* Marketplace */}
        <div className="flex gap-1.5">
          <button onClick={() => setMarketplace('')}
            className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
              !marketplace ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}>All</button>
          {availableMarkets.map(id => (
            <button key={id} onClick={() => setMarketplace(id === marketplace ? '' : id)}
              className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                marketplace === id ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}>{MARKETPLACE_LABELS[id] ?? id}</button>
          ))}
        </div>
        {/* Product */}
        <select value={selectedGroup} onChange={e => { setSelectedGroup(e.target.value); setSelectedAsin(''); }}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm">
          <option value="">All Products</option>
          {productGroups.map(g => (
            <option key={g.label} value={g.label}>{g.label} ({g.products.length})</option>
          ))}
        </select>
        {activeGroup && activeGroup.products.length > 1 && (
          <select value={selectedAsin} onChange={e => setSelectedAsin(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm">
            <option value="">All Variations</option>
            {activeGroup.products.map(p => {
              const variant = p.title.match(/\(([^)]+)\)$/)?.[1] ?? p.asin;
              return <option key={p.asin} value={p.asin}>{variant}</option>;
            })}
          </select>
        )}
        <div className="ml-auto">
          <RowConfig config={rowConfig} allRows={ALL_ROWS} onChange={setRowConfig} />
        </div>
      </div>

      {/* Tabs: Hourly / Day of Week */}
      <div className="flex items-center gap-4">
        <div className="border-b border-gray-200 flex gap-6">
          <button onClick={() => setTab('hourly')}
            className={`whitespace-nowrap border-b-2 pb-3 pt-1 text-sm font-medium transition-colors ${
              tab === 'hourly' ? 'border-brand-500 text-brand-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>Hourly Sales</button>
          <button onClick={() => setTab('dow')}
            className={`whitespace-nowrap border-b-2 pb-3 pt-1 text-sm font-medium transition-colors ${
              tab === 'dow' ? 'border-brand-500 text-brand-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>Day of Week</button>
        </div>
        <p className="text-xs text-gray-400 ml-auto">
          Averaged over {totalDays} days &middot; All amounts in USD
        </p>
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center text-gray-400">Loading...</div>
      ) : tab === 'hourly' ? (
        <>
          {/* Peak hour */}
          {hourData.length > 0 && (
            <div className="p-3 bg-blue-50 rounded-lg text-sm">
              Peak sales hour: <strong>{hourLabel(peakHour)}</strong> — {fmtUsd(hourData[peakHour]?.avgRevenue ?? 0)}/day avg
            </div>
          )}

          {/* Hourly bar chart */}
          {hourData.length > 0 && (
            <div className="overflow-x-auto border rounded-xl bg-white p-4">
              <svg width={Math.max(hourData.length * 36, 600)} height={160} className="block">
                {hourData.map((h, i) => {
                  const barH = (h.avgRevenue / maxHourRevenue) * 120;
                  const x = 40 + i * 32;
                  const isPeak = h.hour === peakHour;
                  return (
                    <g key={h.hour}>
                      <rect x={x} y={130 - barH} width={24} height={barH}
                        fill={isPeak ? '#2563eb' : '#93c5fd'} rx={2} />
                      <text x={x + 12} y={148} textAnchor="middle" fontSize={8} fill="#6b7280">{hourLabel(h.hour)}</text>
                      <text x={x + 12} y={126 - barH} textAnchor="middle" fontSize={7} fill="#6b7280">
                        {h.salesPct > 0 ? `${h.salesPct.toFixed(0)}%` : ''}
                      </text>
                    </g>
                  );
                })}
              </svg>
            </div>
          )}

          {/* Hourly data table */}
          {hourData.length > 0 && (
            <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="text-left px-3 py-2 sticky left-0 bg-gray-50 z-10 min-w-[100px]">Metric</th>
                    {hourData.map(h => (
                      <th key={h.hour} className={`text-right px-2 py-2 min-w-[52px] whitespace-nowrap ${h.hour === peakHour ? 'bg-blue-50' : ''}`}>
                        {hourLabel(h.hour)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map(row => (
                    <tr key={row.key} className="border-b hover:bg-gray-50/50">
                      <td className="px-3 py-1.5 font-medium text-gray-700 sticky left-0 bg-white z-10">{row.label}</td>
                      {hourData.map(h => {
                        const val = row.getHourValue(h);
                        const isPeak = h.hour === peakHour;
                        return (
                          <td key={h.hour} className={`px-2 py-1.5 text-right tabular-nums ${isPeak ? 'bg-blue-50 font-medium' : ''}`}>
                            {row.barMax ? (
                              <div className="relative">
                                <div className="absolute inset-0 opacity-20 rounded"
                                  style={{ background: row.barColor, width: `${(val / row.barMax) * 100}%`, height: '100%' }} />
                                <span className="relative">{row.format(val)}</span>
                              </div>
                            ) : row.format(val)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <>
          {/* Day of Week bar chart */}
          {dowData.length > 0 && (() => {
            const peakDow = dowData.reduce((max, d) => d.avgRevenue > max.avgRevenue ? d : max, dowData[0]);
            return (
              <>
                <div className="p-3 bg-blue-50 rounded-lg text-sm">
                  Best day: <strong>{peakDow.name}</strong> — {fmtUsd(peakDow.avgRevenue)}/week avg
                </div>

                <div className="overflow-x-auto border rounded-xl bg-white p-4">
                  <svg width={400} height={180} className="block mx-auto">
                    {dowData.map((d, i) => {
                      const barH = (d.avgRevenue / maxDowRevenue) * 130;
                      const x = 30 + i * 50;
                      const isPeak = d.dow === peakDow.dow;
                      return (
                        <g key={d.dow}>
                          <rect x={x} y={145 - barH} width={36} height={barH}
                            fill={isPeak ? '#2563eb' : '#93c5fd'} rx={3} />
                          <text x={x + 18} y={165} textAnchor="middle" fontSize={11} fill="#374151" fontWeight={isPeak ? 'bold' : 'normal'}>
                            {d.name}
                          </text>
                          <text x={x + 18} y={140 - barH} textAnchor="middle" fontSize={9} fill="#6b7280">
                            {fmtUsd(d.avgRevenue)}
                          </text>
                        </g>
                      );
                    })}
                  </svg>
                </div>

                {/* Day of Week data table */}
                <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b text-xs font-semibold uppercase tracking-wider text-gray-500">
                        <th className="text-left px-4 py-3 sticky left-0 bg-gray-50 z-10">Metric</th>
                        {dowData.map(d => (
                          <th key={d.dow} className={`text-right px-4 py-3 ${d.dow === peakDow.dow ? 'bg-blue-50' : ''}`}>
                            {d.name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {visibleRows.map(row => (
                        <tr key={row.key} className="border-b hover:bg-gray-50/50">
                          <td className="px-4 py-2 font-medium text-gray-700 sticky left-0 bg-white z-10 text-xs">{row.label}</td>
                          {dowData.map(d => {
                            const val = row.getDowValue(d);
                            const isPeak = d.dow === peakDow.dow;
                            return (
                              <td key={d.dow} className={`px-4 py-2 text-right tabular-nums text-xs ${isPeak ? 'bg-blue-50 font-medium' : ''}`}>
                                {row.barMax ? (
                                  <div className="relative">
                                    <div className="absolute inset-0 opacity-20 rounded"
                                      style={{ background: row.barColor, width: `${(val / row.barMax) * 100}%`, height: '100%' }} />
                                    <span className="relative">{row.format(val)}</span>
                                  </div>
                                ) : row.format(val)}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            );
          })()}
        </>
      )}
    </div>
  );
}
